import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as XLSX from 'xlsx';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import {
  Plus, Search, MoreVertical, Edit, Trash2, Lock,
  Users, MapPin, FileDown, X, ChevronLeft, ChevronRight,
  ChevronRight as ArrowRight, UserCheck, UserX,
  Upload, CheckCircle2, AlertTriangle, XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import EmptyState from '../components/EmptyState';

const SAUDE_CONFIG = {
  ativo:           { label: 'Ativo',           cls: 'bg-emerald-100 text-emerald-700 border-emerald-300', color: '#22C55E', desc: '≤ 30 dias' },
  inativo_recente: { label: 'Inativo Recente', cls: 'bg-amber-100 text-amber-700 border-amber-300',      color: '#EAB308', desc: '31 – 90 dias' },
  inativo_antigo:  { label: 'Inativo Antigo',  cls: 'bg-red-100 text-red-700 border-red-300',            color: '#EF4444', desc: '> 90 dias' },
  prospect:        { label: 'Prospect',         cls: 'bg-slate-100 text-slate-500 border-slate-300',      color: '#94A3B8', desc: 'Sem pedidos' },
};

const DonutChart = ({ segments, total, label }) => {
  const r = 35;
  const cx = 50;
  const cy = 50;
  const C = 2 * Math.PI * r;

  if (total === 0) {
    return (
      <svg viewBox="0 0 100 100" className="w-full h-full">
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="#E2E8F0" strokeWidth="14"/>
        <text x={cx} y={cy + 2} textAnchor="middle" style={{ fontSize: 14, fontWeight: 900, fill: '#94A3B8' }}>0</text>
        {label && (
          <text x={cx} y={cy + 13} textAnchor="middle" style={{ fontSize: 7, fontWeight: 600, fill: '#94A3B8' }}>{label}</text>
        )}
      </svg>
    );
  }

  let accumulated = 0;
  return (
    <svg viewBox="0 0 100 100" className="w-full h-full">
      {segments.map((seg, i) => {
        const arcLen = (seg.value / total) * C;
        const offset = -accumulated;
        accumulated += arcLen;
        return (
          <circle
            key={i}
            cx={cx} cy={cy} r={r}
            fill="none"
            stroke={seg.color}
            strokeWidth="14"
            strokeDasharray={`${arcLen} ${C}`}
            strokeDashoffset={offset}
            transform={`rotate(-90 ${cx} ${cy})`}
          />
        );
      })}
      <text x={cx} y={cy + (label ? 2 : 5)} textAnchor="middle" style={{ fontSize: 14, fontWeight: 900, fill: '#1e293b' }}>
        {total}
      </text>
      {label && (
        <text x={cx} y={cy + 13} textAnchor="middle" style={{ fontSize: 7, fontWeight: 600, fill: '#94A3B8' }}>{label}</text>
      )}
    </svg>
  );
};

const Clientes = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const tableRef = useRef(null);
  const [searchParams] = useSearchParams();

  const [clientes, setClientes] = useState([]);
  const [pedidosTodos, setPedidosTodos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [saudeFilter, setSaudeFilter] = useState(null);
  const [naoPositivadosFilter, setNaoPositivadosFilter] = useState(() => searchParams.get('filtro') === 'nao_positivados');
  const [inativosFilter, setInativosFilter] = useState(() => searchParams.get('filtro') === 'inativos');
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0 });
  const importInputRef = useRef(null);

  const mesAtual = (() => {
    const s = new Date().toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
    return s.charAt(0).toUpperCase() + s.slice(1);
  })();

  useEffect(() => { fetchAll(); }, []);
  useEffect(() => { setCurrentPage(1); }, [searchTerm, saudeFilter, naoPositivadosFilter, inativosFilter]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [rClientes, rPedidos] = await Promise.all([
        api.get('/clientes'),
        api.get('/pedidos'),
      ]);
      setClientes(Array.isArray(rClientes.data) ? rClientes.data : []);
      setPedidosTodos(Array.isArray(rPedidos.data) ? rPedidos.data : []);
    } catch {
      toast.error('Erro ao carregar dados');
      setClientes([]);
      setPedidosTodos([]);
    } finally {
      setLoading(false);
    }
  };

  // Map: cliente_id → timestamp de referência do pedido mais recente.
  // Usa data_entrega (YYYY-MM-DD) como campo primário: entregas futuras
  // resultam em dias negativos → classificados como "ativo", o que é correto
  // para um cliente com entrega agendada. Fallback para criado_em se ausente.
  const saudeMap = useMemo(() => {
    const map = {};
    pedidosTodos.forEach(p => {
      if (!p.cliente_id) return;
      let t = 0;
      if (p.data_entrega) {
        t = new Date(p.data_entrega + 'T00:00:00').getTime();
      } else if (p.criado_em) {
        t = new Date(p.criado_em).getTime();
      }
      if (isNaN(t) || t === 0) return;
      if (!map[p.cliente_id] || t > map[p.cliente_id]) {
        map[p.cliente_id] = t;
      }
    });
    return map;
  }, [pedidosTodos]);

  // Clientes que fizeram ao menos 1 pedido não-cancelado no mês corrente
  const positivadosMesSet = useMemo(() => {
    const mesN = new Date().getMonth();
    const anoN = new Date().getFullYear();
    return new Set(
      pedidosTodos
        .filter(p => {
          if (p.status === 'CANCELADO') return false;
          const d = new Date(p.criado_em);
          return d.getMonth() === mesN && d.getFullYear() === anoN;
        })
        .map(p => p.cliente_id)
        .filter(Boolean)
    );
  }, [pedidosTodos]);

  const classifyCliente = (clienteId) => {
    const lastTs = saudeMap[clienteId];
    if (!lastTs) return 'prospect';
    const days = Math.floor((Date.now() - lastTs) / 86400000);
    if (days <= 30)  return 'ativo';
    if (days <= 90)  return 'inativo_recente';
    return 'inativo_antigo';
  };

  const saudeContadores = useMemo(() => {
    const counts = { ativo: 0, inativo_recente: 0, inativo_antigo: 0, prospect: 0 };
    clientes.forEach(c => { counts[classifyCliente(c.id)]++; });
    return counts;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientes, saudeMap]);

  const filteredClientes = useMemo(() => {
    let result = clientes.filter(c => {
      if (searchTerm) {
        const s = searchTerm.toLowerCase();
        const match =
          c.nome?.toLowerCase().includes(s) ||
          c.nome_fantasia?.toLowerCase().includes(s) ||
          c.cnpj?.toLowerCase().includes(s) ||
          c.cidade?.toLowerCase().includes(s) ||
          c.comprador?.toLowerCase().includes(s);
        if (!match) return false;
      }
      if (saudeFilter && classifyCliente(c.id) !== saudeFilter) return false;
      if (naoPositivadosFilter && positivadosMesSet.has(c.id)) return false;
      if (inativosFilter) {
        const lastTs = saudeMap[c.id];
        if (!lastTs) return false;
        const dias = Math.floor((Date.now() - lastTs) / 86400000);
        if (dias <= 60) return false;
      }
      return true;
    });
    if (inativosFilter) {
      result = [...result].sort((a, b) => {
        const dA = saudeMap[a.id] ? Math.floor((Date.now() - saudeMap[a.id]) / 86400000) : 0;
        const dB = saudeMap[b.id] ? Math.floor((Date.now() - saudeMap[b.id]) / 86400000) : 0;
        return dB - dA;
      });
    }
    return result;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientes, searchTerm, saudeFilter, naoPositivadosFilter, inativosFilter, saudeMap, positivadosMesSet]);

  const totalPages = Math.ceil(filteredClientes.length / itemsPerPage);
  const clientesPagina = filteredClientes.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const handleDelete = async (id) => {
    if (!isAdmin) return;
    if (window.confirm('Excluir este cliente permanentemente?')) {
      try {
        await api.delete(`/clientes/${id}`);
        toast.success('Registro removido');
        fetchAll();
      } catch { toast.error('Erro ao deletar'); }
    }
  };

  const handleExportPDF = async () => {
    setPdfLoading(true);
    try {
      const r = await api.post('/export/clientes-pdf', { ids: filteredClientes.map(c => c.id) }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `clientes_${new Date().toLocaleDateString('pt-BR').replace(/\//g, '-')}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Erro ao gerar PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  const handleImportarPlanilha = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';

    let importados = 0, duplicados = 0, erros = 0;
    const dupsDetail = [], errosDetail = [];
    let totalRows = 0;

    try {
      const buffer = await file.arrayBuffer();
      const wb = XLSX.read(buffer, { type: 'array' });
      const sheetName = wb.SheetNames.includes('Clientes') ? 'Clientes' : wb.SheetNames[0];
      const rows = XLSX.utils.sheet_to_json(wb.Sheets[sheetName], { defval: '' });

      if (!rows.length) { toast.error('Planilha vazia ou sem dados.'); return; }

      totalRows = rows.length;
      setImportLoading(true);
      setImportProgress({ current: 0, total: totalRows });

      const existingCNPJs = new Set(clientes.map(c => (c.cnpj || '').replace(/[.\-/]/g, '').trim()));

      const get = (row, key) => {
        const k = Object.keys(row).find(k => k.trim().toUpperCase() === key);
        return k ? String(row[k] || '').trim() : '';
      };

      for (let i = 0; i < rows.length; i++) {
        setImportProgress({ current: i + 1, total: totalRows });
        const row = rows[i];
        const cnpjRaw = get(row, 'CNPJ');
        const razaoSocial = get(row, 'RAZAO_SOCIAL');

        if (!cnpjRaw || !razaoSocial) {
          erros++;
          errosDetail.push(`Linha ${i + 2}: CNPJ ou RAZAO_SOCIAL ausente`);
          continue;
        }

        const cnpjNorm = cnpjRaw.replace(/[.\-/]/g, '').trim();
        if (existingCNPJs.has(cnpjNorm)) {
          duplicados++;
          dupsDetail.push(`${razaoSocial} — CNPJ ${cnpjRaw}`);
          continue;
        }

        try {
          await api.post('/clientes', {
            nome: razaoSocial,
            nome_fantasia: get(row, 'NOME_FANTASIA'),
            cnpj: cnpjRaw,
            telefone: get(row, 'TELEFONE'),
            email: get(row, 'EMAIL'),
            cidade: get(row, 'CIDADE'),
            estado: get(row, 'UF'),
            cep: get(row, 'CEP'),
            endereco: get(row, 'ENDERECO'),
          });
          importados++;
          existingCNPJs.add(cnpjNorm);
        } catch (err) {
          if (err.response?.status === 409) {
            duplicados++;
            dupsDetail.push(`${razaoSocial} — CNPJ ${cnpjRaw}`);
          } else {
            erros++;
            errosDetail.push(`${razaoSocial}: ${err.response?.data?.detail || 'Erro desconhecido'}`);
          }
        }
      }

      if (importados > 0) fetchAll();
      setImportResult({ total: totalRows, importados, duplicados, erros, dupsDetail, errosDetail });
    } catch {
      toast.error('Erro ao processar planilha. Verifique se é um .xlsx válido.');
    } finally {
      setImportLoading(false);
    }
  };

  if (loading) return (
    <div className="flex flex-col items-center justify-center h-[80vh] gap-4">
      <div className="w-10 h-10 border-4 border-slate-200 border-t-[#0A3D73] rounded-full animate-spin"/>
      <p className="text-xs font-black uppercase tracking-widest text-slate-400 animate-pulse">Carregando Clientes...</p>
    </div>
  );

  const donutSegments = [
    { value: saudeContadores.ativo,           color: SAUDE_CONFIG.ativo.color },
    { value: saudeContadores.inativo_recente, color: SAUDE_CONFIG.inativo_recente.color },
    { value: saudeContadores.inativo_antigo,  color: SAUDE_CONFIG.inativo_antigo.color },
    { value: saudeContadores.prospect,        color: SAUDE_CONFIG.prospect.color },
  ];

  const handleDetalharCarteira = () => {
    setSaudeFilter(null);
    tableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const healthPanel = (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      <div className="px-5 pt-5 pb-4 border-b border-slate-100">
        <p className="text-[10px] font-black uppercase tracking-widest text-[#0A3D73]">Carteira de Clientes</p>
        <p className="text-xs text-slate-400 font-semibold mt-0.5">{mesAtual}</p>
      </div>

      <div className="flex justify-center py-6 px-5">
        <div className="w-40 h-40">
          <DonutChart segments={donutSegments} total={clientes.length} label="clientes" />
        </div>
      </div>

      <div className="px-3 pb-3 space-y-0.5">
        {Object.entries(SAUDE_CONFIG).map(([key, cfg]) => {
          const count = saudeContadores[key] || 0;
          const isActive = saudeFilter === key;
          return (
            <button
              key={key}
              onClick={() => setSaudeFilter(isActive ? null : key)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all ${
                isActive
                  ? 'bg-[#0A3D73]/5 ring-1 ring-[#0A3D73]/15'
                  : 'hover:bg-slate-50'
              }`}
            >
              <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: cfg.color }}/>
              <span className="flex-1 text-xs font-bold text-slate-600">{cfg.label}</span>
              <span className="text-sm font-black tabular-nums" style={{ color: cfg.color }}>{count}</span>
              <ArrowRight size={11} className="text-slate-300 flex-shrink-0"/>
            </button>
          );
        })}
      </div>

      <div className="px-4 py-4 border-t border-slate-100">
        <button
          onClick={handleDetalharCarteira}
          className="w-full flex items-center justify-center gap-2 bg-[#0A3D73] hover:bg-[#082D54] text-white text-xs font-black uppercase px-4 h-9 rounded-lg transition-colors"
        >
          Detalhar Carteira <ArrowRight size={12}/>
        </button>
      </div>
    </div>
  );

  return (
    <div className="p-4 md:p-6 bg-[#E9EEF2] min-h-screen">

      {/* HEADER */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-3">
        <div>
          <h1 className="text-2xl font-black text-slate-900">Clientes</h1>
          <p className="text-xs text-slate-500 font-semibold mt-0.5">Gestão de Carteira · {filteredClientes.length} registros</p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <div className="flex items-center bg-white border border-slate-200 rounded-lg h-9 px-3 gap-2 shadow-sm">
            <Search size={13} className="text-slate-400"/>
            <input
              placeholder="Nome, CNPJ, cidade..."
              className="text-xs font-semibold bg-transparent outline-none w-44 text-slate-700"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
          {saudeFilter && (
            <button
              onClick={() => setSaudeFilter(null)}
              className="flex items-center gap-1.5 bg-[#0A3D73]/10 border border-[#0A3D73]/20 text-[#0A3D73] text-xs font-black uppercase px-3 h-9 rounded-lg transition-colors hover:bg-[#0A3D73]/20"
            >
              <span className="w-2 h-2 rounded-full" style={{ background: SAUDE_CONFIG[saudeFilter].color }}/>
              {SAUDE_CONFIG[saudeFilter].label}
              <X size={11}/>
            </button>
          )}
          {inativosFilter && (
            <button
              onClick={() => setInativosFilter(false)}
              className="flex items-center gap-1.5 bg-red-100 border border-red-200 text-red-700 text-xs font-black uppercase px-3 h-9 rounded-lg transition-colors hover:bg-red-200"
            >
              <UserX size={11}/>
              Inativos
              <X size={11}/>
            </button>
          )}
          <button
            onClick={() => setInativosFilter(v => !v)}
            className={`flex items-center gap-2 border text-xs font-black uppercase px-4 h-9 rounded-lg shadow-sm transition-colors ${
              inativosFilter
                ? 'bg-red-600 text-white border-red-600'
                : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
            }`}
          >
            <UserX size={14}/> Inativos
          </button>
          {naoPositivadosFilter && (
            <button
              onClick={() => setNaoPositivadosFilter(false)}
              className="flex items-center gap-1.5 bg-slate-200 border border-slate-300 text-slate-600 text-xs font-black uppercase px-3 h-9 rounded-lg transition-colors hover:bg-slate-300"
            >
              <UserCheck size={11}/>
              Não positivados
              <X size={11}/>
            </button>
          )}
          <button
            onClick={() => setNaoPositivadosFilter(v => !v)}
            className={`flex items-center gap-2 border text-xs font-black uppercase px-4 h-9 rounded-lg shadow-sm transition-colors ${
              naoPositivadosFilter
                ? 'bg-[#0A3D73] text-white border-[#0A3D73]'
                : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
            }`}
          >
            <UserCheck size={14}/> Não positivados
          </button>
          <button
            onClick={handleExportPDF}
            disabled={pdfLoading}
            className="flex items-center gap-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-xs font-black uppercase px-4 h-9 rounded-lg shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FileDown size={14}/> {pdfLoading ? 'Gerando...' : 'PDF'}
          </button>
          {isAdmin ? (
            <>
              <button
                onClick={() => importInputRef.current?.click()}
                disabled={importLoading}
                className="flex items-center gap-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-xs font-black uppercase px-4 h-9 rounded-lg shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Upload size={14}/> Importar Planilha
              </button>
              <button
                onClick={() => navigate('/clientes/novo')}
                className="flex items-center gap-2 bg-[#0A3D73] hover:bg-[#082D54] text-white text-xs font-black uppercase px-4 h-9 rounded-lg shadow-sm transition-colors"
              >
                <Plus size={14}/> Novo Cliente
              </button>
            </>
          ) : (
            <div className="flex items-center gap-2 bg-white border border-slate-200 px-3 py-2 text-slate-400 text-xs font-bold rounded-lg">
              <Lock size={12}/> Modo Visualização
            </div>
          )}
        </div>
      </div>

      {/* GRID 2 COLUNAS */}
      <div className="flex flex-col md:flex-row gap-5 items-start">

        {/* ── COLUNA ESQUERDA (70%) ── */}
        <div className="w-full md:flex-1 min-w-0 space-y-5">

          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {[
              { label: 'Total de Clientes',  value: clientes.length,                                                  icon: Users,  cor: '#1E40AF', fundo: '#EFF6FF' },
              { label: 'Resultado da Busca', value: filteredClientes.length,                                           icon: Search, cor: '#374151', fundo: '#F9FAFB' },
              { label: 'Estados Atendidos',  value: [...new Set(clientes.map(c => c.estado).filter(Boolean))].length,  icon: MapPin, cor: '#166534', fundo: '#DCFCE7' },
            ].map((k, i) => (
              <div key={i} className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-bold uppercase tracking-wide text-slate-400">{k.label}</p>
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: k.fundo }}>
                    <k.icon size={13} style={{ color: k.cor }}/>
                  </div>
                </div>
                <p className="text-xl font-black" style={{ color: k.cor }}>{k.value}</p>
              </div>
            ))}
          </div>

          {/* Painel de saúde — apenas mobile */}
          <div className="md:hidden">
            {healthPanel}
          </div>

          {/* TABELA */}
          <div ref={tableRef} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-left">
                <thead>
                  <tr className="bg-[#0A3D73] text-white text-xs font-black uppercase tracking-wide">
                    <th className="px-4 py-3">Cliente</th>
                    <th className="px-4 py-3">Localização</th>
                    {isAdmin && <th className="px-4 py-3 text-center">Ações</th>}
                  </tr>
                </thead>
                <tbody>
                  {filteredClientes.length === 0 ? (
                    <tr>
                      <td colSpan={isAdmin ? 3 : 2}>
                        <EmptyState
                          icon={
                            <svg width="72" height="72" viewBox="0 0 72 72" fill="none">
                              <circle cx="24" cy="24" r="11" stroke="#4A90D9" strokeWidth="2"/>
                              <path d="M4 62c0-11.05 8.95-20 20-20 4.2 0 8.1 1.3 11.3 3.5" stroke="#4A90D9" strokeWidth="2" strokeLinecap="round"/>
                              <circle cx="48" cy="26" r="9" stroke="#7BB8F0" strokeWidth="2"/>
                              <path d="M37.7 55.5A19.9 19.9 0 0148 53c11.05 0 20 8.95 20 20" stroke="#7BB8F0" strokeWidth="2" strokeLinecap="round"/>
                            </svg>
                          }
                          title="Nenhum cliente encontrado"
                          subtitle={saudeFilter
                            ? `Não há clientes na categoria "${SAUDE_CONFIG[saudeFilter].label}"`
                            : 'Comece cadastrando seus primeiros clientes'}
                          actionLabel={isAdmin && !saudeFilter ? '+ Novo Cliente' : undefined}
                          onAction={isAdmin && !saudeFilter ? () => navigate('/clientes/novo') : undefined}
                        />
                      </td>
                    </tr>
                  ) : clientesPagina.map((c, idx) => {
                    const saude = SAUDE_CONFIG[classifyCliente(c.id)];
                    return (
                      <tr
                        key={c.id}
                        onClick={() => navigate(`/clientes/${c.id}`)}
                        className={`text-xs border-b border-slate-100 transition-colors cursor-pointer
                          ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/60'} hover:bg-blue-50`}
                      >
                        <td className="px-4 py-1.5">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-black text-slate-900 uppercase text-xs">{c.nome_fantasia || c.nome}</p>
                            <span className={`inline-block px-1.5 py-0.5 text-[9px] font-black uppercase border rounded-full whitespace-nowrap ${saude.cls}`}>
                              {saude.label}
                            </span>
                            {positivadosMesSet.has(c.id) ? (
                              <span className="inline-block px-1.5 py-0.5 text-[9px] font-black uppercase bg-blue-100 text-blue-700 border border-blue-300 rounded-full whitespace-nowrap">
                                ✓ Mês
                              </span>
                            ) : (
                              <span className="inline-block px-1.5 py-0.5 text-[9px] font-black uppercase bg-slate-100 text-slate-400 border border-slate-200 rounded-full whitespace-nowrap">
                                Pendente
                              </span>
                            )}
                            {c.isento_ipi && (
                              <span className="inline-block px-1.5 py-0.5 text-[9px] font-black uppercase bg-emerald-100 text-emerald-700 border border-emerald-300 rounded-full whitespace-nowrap">
                                Isento IPI
                              </span>
                            )}
                          </div>
                          {c.nome_fantasia && (
                            <p className="text-xs text-slate-400 font-semibold mt-0.5 uppercase">{c.nome}</p>
                          )}
                        </td>
                        <td className="px-4 py-1.5 whitespace-nowrap">
                          <p className="font-bold uppercase text-slate-700">{c.cidade || '---'}{c.estado ? ` — ${c.estado}` : ''}</p>
                          {c.endereco && <p className="text-xs text-slate-400 font-semibold mt-0.5">{c.endereco}</p>}
                        </td>
                        {isAdmin && (
                          <td className="px-4 py-1.5 text-center" onClick={e => e.stopPropagation()}>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button className="h-7 w-7 flex items-center justify-center rounded-lg hover:bg-slate-100 transition-colors">
                                  <MoreVertical size={14} className="text-slate-400"/>
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="rounded-xl border-slate-200 shadow-xl bg-white">
                                <DropdownMenuItem
                                  onClick={() => navigate(`/clientes/editar/${c.id}`)}
                                  className="text-xs font-bold gap-2 cursor-pointer p-3 rounded-lg"
                                >
                                  <Edit size={13} className="text-[#0A3D73]"/> Editar
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => handleDelete(c.id)}
                                  className="text-xs font-bold gap-2 cursor-pointer text-red-600 p-3 rounded-lg"
                                >
                                  <Trash2 size={13}/> Excluir
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-slate-100 bg-white">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-slate-500">Exibir:</span>
                  <select
                    value={itemsPerPage}
                    onChange={e => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                    className="border border-slate-200 rounded-lg text-xs font-bold bg-white px-2 h-8 outline-none focus:border-[#0A3D73] cursor-pointer"
                  >
                    {[10, 20, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#0A3D73] text-white disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#082D54] transition-colors"
                  >
                    <ChevronLeft size={14}/>
                  </button>
                  <span className="text-xs font-black text-slate-600">
                    Página {currentPage} de {totalPages} · {filteredClientes.length} registros
                  </span>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#0A3D73] text-white disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#082D54] transition-colors"
                  >
                    <ChevronRight size={14}/>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── COLUNA DIREITA (30%) — apenas desktop, sticky ── */}
        <div className="hidden md:block w-72 flex-shrink-0">
          <div className="sticky top-6">
            {healthPanel}
          </div>
        </div>

      </div>

      {/* ── IMPORTAÇÃO — input oculto ── */}
      <input
        ref={importInputRef}
        type="file"
        accept=".xlsx"
        className="hidden"
        onChange={handleImportarPlanilha}
      />

      {/* ── IMPORTAÇÃO — overlay de progresso ── */}
      {importLoading && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
            <div className="text-center mb-4">
              <div className="w-10 h-10 border-4 border-slate-200 border-t-[#0A3D73] rounded-full animate-spin mx-auto mb-3"/>
              <p className="text-sm font-black text-slate-700">Importando planilha...</p>
              <p className="text-xs text-slate-400 mt-1 font-semibold">{importProgress.current} de {importProgress.total} linhas</p>
            </div>
            <div className="w-full bg-slate-100 rounded-full h-2.5">
              <div
                className="bg-[#0A3D73] h-2.5 rounded-full transition-all duration-200"
                style={{ width: importProgress.total ? `${(importProgress.current / importProgress.total) * 100}%` : '0%' }}
              />
            </div>
          </div>
        </div>
      )}

      {/* ── IMPORTAÇÃO — modal de resultado ── */}
      {importResult && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <h2 className="text-sm font-black uppercase tracking-wide text-slate-800">Resultado da Importação</h2>
              <button onClick={() => setImportResult(null)} className="p-1 rounded-lg hover:bg-slate-100 transition-colors">
                <X size={16} className="text-slate-400"/>
              </button>
            </div>
            <div className="p-6 space-y-2.5">
              <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-xl border border-emerald-100">
                <CheckCircle2 size={18} className="text-emerald-600 flex-shrink-0"/>
                <p className="text-sm font-black text-emerald-700">✅ {importResult.importados} importado(s) com sucesso</p>
              </div>
              <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-xl border border-amber-100">
                <AlertTriangle size={18} className="text-amber-600 flex-shrink-0"/>
                <p className="text-sm font-black text-amber-700">⚠️ {importResult.duplicados} ignorado(s) — já existia(m)</p>
              </div>
              <div className="flex items-center gap-3 p-3 bg-red-50 rounded-xl border border-red-100">
                <XCircle size={18} className="text-red-600 flex-shrink-0"/>
                <p className="text-sm font-black text-red-700">❌ {importResult.erros} erro(s)</p>
              </div>

              {importResult.dupsDetail.length > 0 && (
                <div className="pt-1">
                  <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1.5">Ignorados (já existiam)</p>
                  <div className="max-h-28 overflow-y-auto space-y-1 pr-1">
                    {importResult.dupsDetail.map((d, i) => (
                      <p key={i} className="text-xs text-amber-700 bg-amber-50 border border-amber-100 px-3 py-1.5 rounded-lg">{d}</p>
                    ))}
                  </div>
                </div>
              )}

              {importResult.errosDetail.length > 0 && (
                <div className="pt-1">
                  <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1.5">Detalhes dos Erros</p>
                  <div className="max-h-28 overflow-y-auto space-y-1 pr-1">
                    {importResult.errosDetail.map((e, i) => (
                      <p key={i} className="text-xs text-red-700 bg-red-50 border border-red-100 px-3 py-1.5 rounded-lg">{e}</p>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="px-6 pb-5">
              <button
                onClick={() => setImportResult(null)}
                className="w-full bg-[#0A3D73] hover:bg-[#082D54] text-white text-xs font-black uppercase h-9 rounded-lg transition-colors"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Clientes;
